import { ApolloError, Reference, gql, useMutation } from "@apollo/client";
import { useAuthContext } from "contexts";
import {
  GET_EMPLOYEE_ALL_NOTIFICATIONS,
  GET_MANAGER_ALL_NOTIFICATIONS,
} from "hooks";
import {useCallback, useMemo} from "react";
import { toast } from "react-toastify";
import {
  NOTIFICATION_CATEGORY,
  NOTIFICATION_STATUS,
  NotificationAssignment,
} from "types/notifications";

interface UpdateAssignedNotificationsData {
  updatedAssignedNotifications: {
    result: Pick<
      NotificationAssignment,
      "id" | "status" | "modifiedAt" | "reviewedAt"
    >[];
  };
}
interface UpdateAssignedNotificationsVars {
  assignedNotifications?: Pick<NotificationAssignment, "id">[] | null;
  categories?: NOTIFICATION_CATEGORY[] | null;
  status: NotificationAssignment["status"];
}

const UPDATE_ASSIGNED_NOTIFICATIONS = gql`
  mutation updateAssignedNotification(
    $assignedNotifications: [UpdateAssignedNotificationsInputType]!
    $status: String!
  ) {
    updatedAssignedNotifications: update_assigned_notifications(
      notification_assignments: $assignedNotifications
      status: $status
    ) {
      result {
        id
        status
        modifiedAt: modified_at
        reviewedAt: reviewed_at
      }
    }
  }
`;

const UPDATE_ASSIGNED_NOTIFICATIONS_BY_CATEGORY = gql`
  mutation updateAssignedNotification(
    $categories: [String]!
    $status: String!
  ) {
    updatedAssignedNotifications: update_assigned_notifications(
      categories: $categories
      status: $status
    ) {
      result {
        id
        status
        modifiedAt: modified_at
        reviewedAt: reviewed_at
      }
    }
  }
`;

export const useUpdateAssignedNotifications = (updateByCategory: boolean) => {
  const { user, isManager } = useAuthContext();
  const employeeId = user?.userId;
  const managerId = user?.managerId;

  const updateQuery = useMemo(() => {
    return updateByCategory ? UPDATE_ASSIGNED_NOTIFICATIONS_BY_CATEGORY : UPDATE_ASSIGNED_NOTIFICATIONS;
  }, [updateByCategory]);

  const [updateNotificationAssignments] = useMutation<
    UpdateAssignedNotificationsData,
    UpdateAssignedNotificationsVars
  >(updateQuery, {
    update(cache, { data }) {
      const notifications = data?.updatedAssignedNotifications.result ?? [];
      if (notifications.length > 0) {
        notifications.forEach((notification) => {
          if (notification.status === NOTIFICATION_STATUS.DELETED) {
            cache.modify({
              fields: {
                notifications_assignments(existing = [], { readField }) {
                  return existing.filter(
                    (ref: Reference) => notification.id !== readField("id", ref)
                  );
                },
              },
            });
          } else {
            cache.writeFragment({
              id: `notifications_assignments:${notification.id}`,
              fragment: gql`
                fragment Notification on notifications_assignments {
                  status
                  modified_at
                  reviewed_at
                }
              `,
              data: {
                status: notification.status,
                modified_at: notification.modifiedAt,
                reviewed_at: notification.reviewedAt,
              },
            });
            const query = isManager()
              ? GET_MANAGER_ALL_NOTIFICATIONS
              : GET_EMPLOYEE_ALL_NOTIFICATIONS;
            cache.updateQuery(
              {
                query,
                variables: isManager() ? { managerId } : { employeeId },
              },
              (data) => ({
                unreadCounter: data?.unreadCounter - 1,
                allCounter: data?.allCounter - 1,
                employeeAndProjectsCounter: data?.employeeAndProjectsCounter - 1,
                timesheetsCounter: data?.timesheetsCounter - 1,
                processesCounter: data?.processesCounter - 1
              })
            );
          }
        });
      }
    },
  });

  const updateAssignedNotifications = useCallback(
    async (variables: UpdateAssignedNotificationsVars) => {
      try {
        await updateNotificationAssignments({ variables });
      } catch (ex) {
        toast.error((ex as ApolloError)?.message);
      }
    },
    [updateNotificationAssignments]
  );

  return { updateAssignedNotifications };
};
